/**
 * ResearchProgressStepper
 *
 * A **vertical** progress stepper that tracks the four stages of the research
 * pipeline.  Active phases pulse, completed phases show a checkmark.
 *
 * After the full response arrives (`isCompleted`) the stepper collapses to a
 * compact summary row.  Clicking it expands the full trace view, which
 * includes any o1 thinking-traces collected during the evaluation phase.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiSparkleLight,
  PiMagnifyingGlassLight,
  PiScalesLight,
  PiPencilSimpleLineLight,
  PiCheckCircleLight,
  PiCaretDownLight,
  PiCaretRightLight,
  PiBrainLight,
  PiWarningLight,
} from 'react-icons/pi';
import { SourceThinkingTrace } from './types';

/* ------------------------------------------------------------------ */
/*  Phase definitions                                                  */
/* ------------------------------------------------------------------ */

type Phase = 'started' | 'orchestration' | 'retrieval' | 'evaluation' | 'synthesis' | 'completed' | 'failed';

interface StepDef {
  key: Phase;
  label: string;
  icon: React.ReactNode;
}

const STEPS: StepDef[] = [
  { key: 'orchestration', label: 'Analyzing query',     icon: <PiSparkleLight className="w-4 h-4" /> },
  { key: 'retrieval',     label: 'Searching sources',   icon: <PiMagnifyingGlassLight className="w-4 h-4" /> },
  { key: 'evaluation',    label: 'Evaluating findings',  icon: <PiScalesLight className="w-4 h-4" /> },
  { key: 'synthesis',     label: 'Writing analysis',     icon: <PiPencilSimpleLineLight className="w-4 h-4" /> },
];

const PHASE_ORDER: Record<Phase, number> = {
  started: 0,
  orchestration: 1,
  retrieval: 2,
  evaluation: 3,
  synthesis: 4,
  completed: 5,
  failed: -1,
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface Props {
  currentPhase: Phase;
  progress?: number;
  elapsed?: number;
  detail?: string;
  /** o1 thinking traces (populated once evaluation finishes or on completed event) */
  thinkingTraces?: SourceThinkingTrace[];
  /** True when evaluation fell back to raw retrieval text */
  evaluationDegraded?: boolean;
  /** Whether the research flow has finished (triggers collapsible mode) */
  isCompleted?: boolean;
}

const ResearchProgressStepper: React.FC<Props> = ({
  currentPhase,
  progress = 0,
  elapsed = 0,
  detail,
  thinkingTraces,
  evaluationDegraded,
  isCompleted = false,
}) => {
  const currentIdx = PHASE_ORDER[currentPhase] ?? 0;
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const prettyElapsed = useMemo(() => {
    if (elapsed < 60) return `${elapsed}s`;
    return `${Math.floor(elapsed / 60)}m ${elapsed % 60}s`;
  }, [elapsed]);

  /* ── Collapsed summary when flow is done ── */
  if (isCompleted) {
    return (
      <div className="mb-3 select-none">
        <button
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors group"
        >
          {expanded ? <PiCaretDownLight className="w-3.5 h-3.5" /> : <PiCaretRightLight className="w-3.5 h-3.5" />}
          <PiCheckCircleLight className="w-3.5 h-3.5 text-emerald-500" />
          <span>{t('Research completed')}{elapsed > 0 ? ` ${t('in')} ${prettyElapsed}` : ''}</span>
          {evaluationDegraded && (
            <span className="inline-flex items-center gap-0.5 text-amber-500">
              <PiWarningLight className="w-3 h-3" /> {t('degraded')}
            </span>
          )}
        </button>

        {expanded && (
          <div className="mt-2 ml-5 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-2">
            {/* Step summary */}
            {STEPS.map((step) => (
              <div key={step.key} className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <PiCheckCircleLight className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                <span>{t(step.label)}</span>
                {step.key === 'evaluation' && evaluationDegraded && (
                  <span className="text-amber-500 text-[10px]">{t('(fallback)')}</span>
                )}
              </div>
            ))}

            {/* Thinking traces */}
            {thinkingTraces && thinkingTraces.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                  <PiBrainLight className="w-3.5 h-3.5" />
                  <span>{t('Evaluation reasoning')}</span>
                </div>
                {thinkingTraces.map((st, i) => (
                  <div key={i} className="ml-1 mb-1.5">
                    <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                      {st.source || t('unknown')}
                    </span>
                    {(Array.isArray(st.traces) ? st.traces : []).map((tr, j) => (
                      <div key={j} className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                        {tr.type === 'reasoning_effort' && tr.reasoning_tokens != null && (
                          <span>{tr.reasoning_tokens.toLocaleString()} {t('reasoning tokens')}</span>
                        )}
                        {tr.type === 'reasoning_content' && tr.content && (
                          <p className="whitespace-pre-wrap leading-relaxed">{tr.content}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  /* ── Live vertical stepper ── */
  return (
    <div className="mb-4 select-none">
      <div className="space-y-0">
        {STEPS.map((step, idx) => {
          const stepIdx = PHASE_ORDER[step.key];
          const isStepCompleted = currentIdx > stepIdx;
          const isActive = currentIdx === stepIdx;
          const isPending = currentIdx < stepIdx;

          return (
            <div key={step.key} className="flex items-stretch gap-3">
              {/* Vertical line + circle column */}
              <div className="flex flex-col items-center w-7">
                {/* Circle */}
                <div
                  className={`
                    flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0
                    transition-all duration-500 ease-out
                    ${isStepCompleted
                      ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400'
                      : isActive
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 ring-2 ring-blue-300/50 dark:ring-blue-600/50'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
                    }
                  `}
                >
                  {isStepCompleted ? (
                    <PiCheckCircleLight className="w-4 h-4" />
                  ) : (
                    <span className={isActive ? 'animate-fadePulse' : ''}>{step.icon}</span>
                  )}
                </div>
                {/* Connector line */}
                {idx < STEPS.length - 1 && (
                  <div
                    className={`w-px flex-1 min-h-[12px] transition-all duration-500 ${
                      isStepCompleted ? 'bg-emerald-400 dark:bg-emerald-500' :
                      isActive ? 'bg-gradient-to-b from-blue-400 to-gray-200 dark:from-blue-500 dark:to-gray-700' :
                      'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>

              {/* Label column */}
              <div className={`pb-2 pt-1 ${isPending ? 'opacity-40' : ''} transition-opacity duration-500`}>
                <span className={`text-sm font-medium ${
                  isStepCompleted ? 'text-emerald-600 dark:text-emerald-400' :
                  isActive ? 'text-gray-800 dark:text-gray-200' :
                  'text-gray-400 dark:text-gray-600'
                }`}>
                  {t(step.label)}
                </span>

                {/* Active phase detail */}
                {isActive && (
                  <div className="flex items-center gap-2 mt-0.5">
                    {/* Spinning dot */}
                    <div className="relative flex items-center justify-center w-3 h-3">
                      <span className="absolute inline-flex h-2.5 w-2.5 rounded-full bg-blue-400/30 dark:bg-blue-500/30 animate-ping" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {detail || t('Processing...')}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                      {prettyElapsed}
                    </span>
                  </div>
                )}

                {/* Show thinking traces in-line for evaluation when available during live flow */}
                {step.key === 'evaluation' && isStepCompleted && thinkingTraces && thinkingTraces.length > 0 && (
                  <div className="mt-1 text-[11px] text-gray-400 dark:text-gray-500 flex items-center gap-1">
                    <PiBrainLight className="w-3 h-3" />
                    {thinkingTraces.reduce((sum, st) => {
                      if (!st?.traces || !Array.isArray(st.traces)) return sum;
                      const rt = st.traces.find(tr => tr.type === 'reasoning_effort');
                      return sum + (rt?.reasoning_tokens || 0);
                    }, 0).toLocaleString()} {t('reasoning tokens')}
                  </div>
                )}

                {/* Degraded evaluation warning */}
                {step.key === 'evaluation' && isStepCompleted && evaluationDegraded && (
                  <div className="mt-0.5 text-[11px] text-amber-500 flex items-center gap-1">
                    <PiWarningLight className="w-3 h-3" />
                    {t('Evaluation timed out — using raw retrieval')}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Thin progress bar */}
      {progress > 0 && (
        <div className="mt-2 ml-10 h-0.5 w-48 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-blue-400 to-blue-500 dark:from-blue-500 dark:to-blue-400 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ResearchProgressStepper;
